import React, { useState } from 'react';
import {
  COLORS,
  SPACING,
  BORDER_RADIUS,
  SHADOWS,
  TRANSITIONS,
  UI_SCREEN_STYLES,
} from '../../styles/UISystem';
import IconBack from '../../components/UISystem/Icons/IconBack';

/**
 * DesignTokensScreen
 *
 * Showcase for design tokens including spacing, border radius, shadows, and transitions
 */
const DesignTokensScreen = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState('spacing');
  const [activeTransition, setActiveTransition] = useState(null);

  // Tab navigation
  const renderTabs = () => {
    const tabs = [
      { id: 'spacing', label: 'Spacing' },
      { id: 'borderRadius', label: 'Border Radius' },
      { id: 'shadows', label: 'Shadows' },
      { id: 'transitions', label: 'Transitions' },
    ];

    return (
      <div
        style={{
          display: 'flex',
          marginBottom: '24px',
          borderBottom: `1px solid ${COLORS.LIGHT_GRAY}`,
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '12px 16px',
              backgroundColor: 'transparent',
              color: activeTab === tab.id ? COLORS.PRIMARY : COLORS.DARK_GRAY,
              border: 'none',
              borderBottom: activeTab === tab.id ? `2px solid ${COLORS.PRIMARY}` : 'none',
              fontWeight: activeTab === tab.id ? '500' : 'normal',
              cursor: 'pointer',
              fontSize: '16px',
              marginBottom: '-1px',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
    );
  };

  // Spacing showcase
  const renderSpacingSection = () => {
    return (
      <div>
        <h3 style={{ marginBottom: '16px' }}>Spacing Tokens</h3>
        <p style={{ marginBottom: '24px' }}>
          Consistent spacing helps create visual rhythm and hierarchy in layouts.
        </p>

        {Object.entries(SPACING).map(([key, value]) => (
          <div key={key} style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div
                style={{
                  width: '100px',
                  fontWeight: '500',
                  color: COLORS.DARK_GRAY,
                }}
              >
                {key}: {value}
              </div>
              <div
                style={{
                  width: value,
                  height: '24px',
                  backgroundColor: COLORS.PRIMARY,
                  marginLeft: '16px',
                }}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Border radius showcase
  const renderBorderRadiusSection = () => {
    return (
      <div>
        <h3 style={{ marginBottom: '16px' }}>Border Radius Tokens</h3>
        <p style={{ marginBottom: '24px' }}>
          Border radius creates softer interfaces and helps establish component identity.
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
          {Object.entries(BORDER_RADIUS).map(([key, value]) => (
            <div key={key} style={{ textAlign: 'center', width: '120px' }}>
              <div
                style={{
                  width: '100px',
                  height: '100px',
                  backgroundColor: COLORS.LIGHT_GRAY,
                  borderRadius: value,
                  margin: '0 auto 12px',
                }}
              />
              <div style={{ fontWeight: '500' }}>{key}</div>
              <div style={{ fontSize: '14px', color: COLORS.MEDIUM_GRAY }}>{value}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Shadows showcase
  const renderShadowsSection = () => {
    return (
      <div>
        <h3 style={{ marginBottom: '16px' }}>Shadow Tokens</h3>
        <p style={{ marginBottom: '24px' }}>Shadows create depth and elevation in the interface.</p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '32px' }}>
          {Object.entries(SHADOWS).map(([key, value]) => (
            <div key={key} style={{ textAlign: 'center', width: '150px' }}>
              <div
                style={{
                  width: '120px',
                  height: '120px',
                  backgroundColor: COLORS.WHITE,
                  boxShadow: value,
                  margin: '0 auto 16px',
                  borderRadius: BORDER_RADIUS.MD,
                }}
              />
              <div style={{ fontWeight: '500' }}>{key}</div>
              <div
                style={{
                  fontSize: '12px',
                  color: COLORS.MEDIUM_GRAY,
                  marginTop: '4px',
                  wordBreak: 'break-word',
                }}
              >
                {value}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Transitions showcase
  const renderTransitionsSection = () => {
    return (
      <div>
        <h3 style={{ marginBottom: '16px' }}>Transition Tokens</h3>
        <p style={{ marginBottom: '24px' }}>
          Transitions add polish and improve the feel of interactive elements.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {Object.entries(TRANSITIONS).map(([key, value]) => (
            <div key={key} style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ width: '100px', fontWeight: '500' }}>{key}</div>
                <div style={{ fontSize: '14px', color: COLORS.MEDIUM_GRAY }}>{value}</div>
              </div>

              <button
                onClick={() => setActiveTransition(activeTransition === key ? null : key)}
                style={{
                  padding: '12px 24px',
                  color: COLORS.WHITE,
                  border: 'none',
                  borderRadius: BORDER_RADIUS.MD,
                  cursor: 'pointer',
                  transition: `transform ${value} ease-in-out, background-color ${value} ease`,
                  transform: activeTransition === key ? 'scale(1.1)' : 'scale(1)',
                  backgroundColor: activeTransition === key ? COLORS.SECONDARY : COLORS.PRIMARY,
                }}
              >
                Click to see {key.toLowerCase()} transition
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render the active tab content
  const renderActiveTabContent = () => {
    switch (activeTab) {
      case 'spacing':
        return renderSpacingSection();
      case 'borderRadius':
        return renderBorderRadiusSection();
      case 'shadows':
        return renderShadowsSection();
      case 'transitions':
        return renderTransitionsSection();
      default:
        return renderSpacingSection();
    }
  };

  return (
    <div style={UI_SCREEN_STYLES.container}>
      {/* Header */}
      <div style={UI_SCREEN_STYLES.header}>
        <IconBack onClick={onBack} />
        <h1 style={UI_SCREEN_STYLES.title}>Design Tokens</h1>
      </div>

      {/* Divider */}
      <div style={UI_SCREEN_STYLES.divider}></div>

      {/* Content */}
      <div style={UI_SCREEN_STYLES.content}>
        {renderTabs()}
        {renderActiveTabContent()}
      </div>
    </div>
  );
};

export default DesignTokensScreen;
